import type { ScheduleAction } from "@/api/types";

/**
 * 행동 결과 플래버 라인 라이브러리.
 *
 * <p>2026-04-23 E18: 주차 해소 직후 WeeklyDigestModal / DiaryTab 등에서
 * "이번 주 결의 결과" 를 서사 톤 한 줄로 보여 주기 위한 기본 라이브러리.
 *
 * <p>각 행동 × 결과 톤(great/success/poor/failure) × 기분 3단계 조합으로
 * 2~3종 라인. weekNo 로 결정론적 shuffle 해서 반복을 줄임. 콘텐츠 팀이 확장하기
 * 쉽게 flat 구조로.
 *
 * <p>자세한 콘텐츠는 docs/ui/weekly-loop-rebuild-plan.md C3 가이드 참조.
 */

export type ActionOutcomeBand = "GREAT_SUCCESS" | "SUCCESS" | "POOR" | "FAILURE";

export interface ActionFlavorContext {
    action: ScheduleAction;
    band: ActionOutcomeBand;
    /** 결정론적 셔플용. */
    weekNo: number;
}

/**
 * action × band 조합에 맞는 플래버 1줄 반환. 복수 배리언트가 있으면 weekNo
 * 기준 순환 선택.
 */
export function pickActionFlavor(ctx: ActionFlavorContext): string {
    const lines = FLAVOR[ctx.action]?.[ctx.band] ?? [];
    if (lines.length === 0) return fallbackLine(ctx.band);
    return lines[ctx.weekNo % lines.length];
}

function fallbackLine(band: ActionOutcomeBand): string {
    switch (band) {
        case "GREAT_SUCCESS": return "예상보다 먼 곳까지 닿았다.";
        case "SUCCESS": return "평범하지만 단단한 한 주였다.";
        case "POOR": return "이번 주는 생각보다 손에 남는 것이 적었다.";
        case "FAILURE": return "계획과는 사뭇 다른 자리에 도착했다.";
    }
}

const FLAVOR: Partial<Record<ScheduleAction, Record<ActionOutcomeBand, string[]>>> = {
    // 2026-04-23 사용자 콘텐츠 우선 배치분 (TRAINING / SCENARIO_EVENT / REST GREAT_SUCCESS·SUCCESS).
    // 나머지는 기존 시드 유지 — 다음 콘텐츠 턴에 CRAFTING/RESEARCH/OUTING/SCOUTING + REST POOR/FAILURE 확장 예정.
    TRAINING: {
        GREAT_SUCCESS: [
            "몸이 오늘의 통증을 기억해 버렸다. 다음 번엔 같은 벽이 더 낮게 느껴질 것이다.",
            "반복이 끝난 자리에 손끝이 먼저 답을 찾아갔다. 이런 날의 훈련은 기술보다 확신을 남긴다.",
            "억지로 올린 성장이 아니다. 몸과 호흡이 같은 박자를 찾은 하루였다.",
        ],
        SUCCESS: [
            "크게 눈부시진 않아도, 오늘의 반복은 분명 어제의 너를 넘어섰다.",
            "조금 더 버티고, 한 번 더 맞추고, 한 걸음 덜 흔들렸다. 이런 축적이 결국 사람을 바꾼다.",
            "몸은 아직 무겁지만, 적어도 같은 실수를 다시 하진 않을 것 같다.",
        ],
        POOR: [
            "분명 움직였는데 남은 건 피로 쪽이 더 컸다. 오늘은 성장보다 소모가 먼저 기억난다.",
            "손은 따라갔지만 몸이 늦었다. 무언가 쌓이긴 했어도 기대만큼은 아니었다.",
            "억지로 밀어붙인 흔적은 남았다. 다만 그 흔적이 실력보다 피곤함에 더 가까웠다.",
        ],
        FAILURE: [
            "오늘의 훈련은 몸을 세우기보다 먼저 무너뜨렸다. 더 밀면 익히는 게 아니라 망가질 것 같다.",
            "반복은 했지만 끝내 리듬을 찾지 못했다. 이런 날은 배운 것보다 놓친 것이 더 선명하다.",
            "억지로 버틴 시간이 전부 헛되진 않겠지만, 적어도 오늘은 성장이라 부르기 어렵다.",
        ],
    },
    REST: {
        GREAT_SUCCESS: [
            "짧은 쉼이었는데도 몸과 정신이 동시에 숨을 돌렸다. 이런 휴식은 하루가 아니라 균형을 되돌린다.",
            "눕고 일어난 뒤에야 비로소 자신이 얼마나 닳아 있었는지 알게 된다. 오늘은 잘 쉬었다.",
            "쉰다는 건 멈추는 게 아니라 다시 버틸 수 있게 되는 일이라는 걸 몸이 먼저 기억했다.",
        ],
        SUCCESS: [
            "크게 달라진 건 없어 보여도, 적어도 오늘 밤은 조금 덜 부서진 채 넘길 수 있겠다.",
            "숨을 고른 덕분에 내일을 받을 자리는 생겼다. 지금은 그걸로 충분하다.",
            "완전한 회복은 아니어도, 더 무너지기 전에 멈춘 건 분명한 이득이다.",
        ],
        POOR: [
            "쉰다는 것도 쉽지 않았다. 몸은 눕지만 머리는 여전히 행군 중이었다.",
            "아무것도 하지 않으려 했는데도, 쉬는 동안 더 많은 생각이 몰려왔다.",
            "피로는 가라앉지 않았고, 잠깐 멈췄다는 사실만 몸에 남았다.",
        ],
        FAILURE: [
            "쉬려 했지만, 잊고 싶지 않은 이름이 자꾸 떠올라 잠 한숨 들지 못했다.",
            "몸은 가만히 있었지만 마음은 끝내 눕지 못했다. 오늘의 쉼은 회복이라 부르기 어렵다.",
            "쉬어야 했는데 오히려 더 지쳤다. 침묵이 생각보다 시끄러운 밤이었다.",
        ],
    },
    CRAFTING: {
        GREAT_SUCCESS: [
            "손끝에서 빚어진 것 하나가, 다음 주의 결투 한 번을 대신 막아 줄 것 같다.",
            "쇠와 가죽과 실이 오늘만큼은 순순히 말을 들었다. 손에서 나온 물건이 곧 확신이 되었다.",
            "작은 도구 하나를 제대로 손본 것뿐인데, 다음 주의 불안을 미리 한 겹 벗겨 낸 기분이다.",
        ],
        SUCCESS: [
            "공방의 불빛 아래에서 장비 한두 점이 단단해졌다.",
            "손은 무난히 움직였고, 결과도 그만큼 정직하게 남았다. 다음 싸움에 들고 갈 만한 물건은 되었다.",
            "완벽하진 않아도 쓸모는 분명하다. 준비란 대개 이런 식으로 쌓인다.",
        ],
        POOR: [
            "재료는 썼지만, 남은 것은 어중간한 형태뿐이다.",
            "무언가를 만들긴 했지만, 손에 쥐는 순간부터 어설픔이 먼저 느껴진다.",
            "공방의 열은 충분했지만 결과는 미지근했다. 다음엔 손을 더 단단히 써야 한다.",
        ],
        FAILURE: [
            "불을 잘못 잡았다. 오늘의 공방은 장부 숫자만 줄여 놓았다.",
            "쓸 수 있는 재료를 썼는데도 남은 건 실패의 흔적뿐이다. 오늘은 만들었다기보다 망쳤다.",
            "조립은 끝났지만 손에 드는 순간 잘못됐다는 걸 알 수 있었다. 공방의 하루가 허무하게 꺼졌다.",
        ],
    },
    OUTING: {
        GREAT_SUCCESS: [
            "낯선 이와의 짧은 대화에서, 뜻밖의 문 하나가 열리는 기척이 있었다.",
            "바깥공기를 쐬러 나섰을 뿐인데, 돌아올 땐 손보다 마음에 더 많은 것을 들고 있었다.",
            "거리에서 흘려들은 말 한 조각이, 의외로 다음 주의 문을 먼저 열어 주었다.",
        ],
        SUCCESS: [
            "오늘은 바깥의 공기를 조금 마셨다. 그것만으로도 다음 한 주가 덜 무거워진 느낌이다.",
            "크게 놀랄 일은 없었어도 방 안에만 있을 때보다 생각이 덜 막혔다.",
            "잠시 자리를 비운 덕분에 마음속 먼지가 조금은 내려앉았다.",
        ],
        POOR: [
            "거리는 넓었지만, 마음에 걸리는 말 한마디는 없었다.",
            "밖으로 나간 시간에 비해 손에 남은 건 적다. 오늘의 외출은 숨 돌림 이상이 되지 못했다.",
            "사람은 많았지만 마주친 건 피로뿐이었다. 발걸음만 괜히 길어졌다.",
        ],
        FAILURE: [
            "바깥은 생각보다 거칠었다. 장부만 얇아졌고, 얼굴에 멍이 하나 생겼다.",
            "기분을 돌리려 나갔다가 오히려 더 피곤한 일만 안고 돌아왔다.",
            "밖에서 찾으려던 답은 없었고, 불필요한 문제만 하나 더 들고 오게 됐다.",
        ],
    },
    RESEARCH: {
        GREAT_SUCCESS: [
            "책상 위에 펼친 지도 한 장이, 마침내 읽히는 언어가 되었다.",
            "오래 붙잡던 단서가 오늘은 끝내 모양을 드러냈다. 지식이 바로 다음 주의 무기가 된다.",
            "문장 하나를 바로잡는 순간 흩어져 있던 조각들이 제자리를 찾았다.",
        ],
        SUCCESS: [
            "답보다 질문이 많아졌다. 그래도 이 질문들이 다음 주를 가볍게 만들 것이다.",
            "확실한 결론까지는 아니어도, 적어도 어디를 더 파야 하는지는 선명해졌다.",
            "읽은 만큼 바로 힘이 되진 않겠지만, 오늘의 정리는 분명 길을 좁혀 준다.",
        ],
        POOR: [
            "몇 시간을 읽었지만, 손에 남는 문장은 고작 두어 줄이었다.",
            "자료는 넘쳤지만 핵심은 끝내 잘 잡히지 않았다. 오늘은 오래 앉아 있었던 시간만 더 선명하다.",
            "단서가 없진 않았으나, 서로 이어지지 않는다. 머릿속이 더 복잡해진 채로 밤이 깊었다.",
        ],
        FAILURE: [
            "머리가 덜그럭거리는 소리만 종일 났다. 책장이 더 어지러워졌을 뿐이다.",
            "자료를 펼칠수록 오히려 길을 잃었다. 오늘의 탐구는 답 대신 피로만 남겼다.",
            "읽고 정리하고 다시 읽었지만 결국 빈칸만 커졌다. 이런 날의 연구는 지식보다 좌절에 가깝다.",
        ],
    },
    SCOUTING: {
        GREAT_SUCCESS: [
            "먼저 본 길이 다음 주의 피 한 방울을 미리 막아 주었다.",
            "짧은 정찰이었지만 돌아왔을 때는 지도 위의 공백이 하나 분명히 메워져 있었다.",
            "누구보다 먼저 본 지형과 흔적이, 아직 오지 않은 위협 하나를 미리 약하게 만들었다.",
        ],
        SUCCESS: [
            "돌아오는 길에 짧게나마 확신이 생겼다. 다음 행군은 조금 덜 두려울 것이다.",
            "완전히 안심할 정도는 아니지만, 적어도 발을 어디에 디뎌야 할지는 더 분명해졌다.",
            "정찰의 성과는 대개 조용하다. 그래도 오늘 본 것들은 분명 다음 결정을 가볍게 만든다.",
        ],
        POOR: [
            "길의 반쪽만 눈에 익혔다. 돌아올 때가 되어서야 놓친 곳이 선명해졌다.",
            "돌아오긴 했지만 확인한 것보다 놓친 것이 더 많아 보인다.",
            "분명 무언가를 봤는데 결정적으로 필요한 부분은 비켜 지나갔다.",
        ],
        FAILURE: [
            "정찰 도중 무언가와 마주쳤다. 정확한 보고보다 먼저 숨이 돌아왔다.",
            "길을 읽으러 나갔다가 오히려 길에 휩쓸렸다. 오늘은 정보보다 불안만 더 커졌다.",
            "정찰이 끝난 뒤에도 마음이 진정되지 않는다. 바깥의 기척을 제대로 읽지 못했다.",
        ],
    },
    SCENARIO_EVENT: {
        GREAT_SUCCESS: [
            "이번 판단은 단순히 잘 넘긴 수준이 아니다. 한 장의 흐름이 네 쪽으로 기울었다.",
            "장면 하나를 지나왔을 뿐인데, 이후 계절 전체의 결이 조금 달라진 것처럼 느껴진다.",
            "이야기는 늘 사람을 시험하지만, 오늘만큼은 네가 먼저 장면의 방향을 붙들었다.",
        ],
        SUCCESS: [
            "적어도 이번 갈림길은 무너지지 않고 건넜다. 다음 장면으로 이어질 발판은 남았다.",
            "완벽하진 않아도 필요한 선택이었다. 오늘의 판단은 이후의 불안을 조금 덜어 낸다.",
            "상처 없이 지나가진 않았지만, 장면은 네 쪽에서 닫혔다.",
        ],
        POOR: [
            "넘기긴 했지만, 장면 끝에 남은 뒷맛이 좋지 않다. 다음 갈림길이 더 무거워질 수 있다.",
            "결정은 끝났지만 흔들림은 남았다. 이야기는 분명 앞으로 이 값을 다시 물을 것이다.",
            "간신히 빠져나왔다는 말이 더 어울린다. 오늘의 선택은 아직 완전히 정리되지 않았다.",
        ],
        FAILURE: [
            "이야기의 주도권이 잠시 네 손에서 벗어났다. 다음 장면은 훨씬 차갑게 시작될 것이다.",
            "이번 갈림길은 분명히 흘려보냈다. 그 여파는 나중이 아니라 바로 다음 장면부터 따라붙을 수 있다.",
            "결정은 끝났지만, 끝난 쪽은 네가 원하던 모습이 아니다.",
        ],
    },
};
